import React, { useState, useEffect, useCallback } from 'react';
import { useRealtimeRefetch } from '../hooks/useRealtimeRefetch';
import apiService, { WatchlistResponse, WatchlistMovie } from '../services/api';
import styled from 'styled-components';

// --- Styled components ---

const Container = styled.div`
  max-width: 900px;
`;

const Layout = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
`;

const Sidebar = styled.aside`
  width: 230px;
  flex-shrink: 0;
  background: var(--color-surface);
  border-radius: 12px;
  box-shadow: var(--shadow-card);
  overflow: hidden;
  position: sticky;
  top: 1rem;
`;

const SidebarHeader = styled.div`
  padding: 1rem 1.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid var(--color-border-subtle);
`;

const ListItem = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  padding: 0.7rem 1.25rem;
  cursor: pointer;
  background: ${p => p.$active ? 'var(--color-accent-subtle)' : 'var(--color-surface)'};
  border-left: 3px solid ${p => p.$active ? 'var(--color-accent)' : 'transparent'};

  &:hover { background: ${p => p.$active ? 'var(--color-accent-subtle)' : 'var(--color-surface-subtle)'}; }
`;

const ListName = styled.span<{ $active: boolean }>`
  flex: 1;
  font-size: 0.9rem;
  font-weight: ${p => p.$active ? '600' : '400'};
  color: ${p => p.$active ? 'var(--color-accent)' : 'var(--color-text)'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const ListCount = styled.span`
  font-size: 0.75rem;
  color: var(--color-text-muted);
  margin-left: 0.4rem;
  flex-shrink: 0;
`;

const ListItemBtns = styled.div`
  display: flex;
  gap: 0.35rem;
  margin-left: 0.5rem;
  flex-shrink: 0;
`;

const ListCardBtn = styled.button<{ $variant?: 'danger' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.85rem;
  white-space: nowrap;
  line-height: 1;

  ${p => p.$variant === 'danger' ? `
    background: #e53e3e;
    color: white;
    transition: background 0.2s;
    &:hover { background: #c53030; }
  ` : `
    background: var(--color-bg);
    color: var(--color-text);
    border: 1px solid var(--color-border);
    transition: background 0.2s;
    &:hover { background: var(--color-border); }

    [data-theme="dark"] & {
      background: rgba(255, 255, 255, 0.09);
      border-color: rgba(255, 255, 255, 0.22);
    }
    [data-theme="dark"] &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.16);
    }
  `}

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ListBtnLabel = styled.span`
  max-width: 0;
  overflow: hidden;
  opacity: 0;
  margin-left: 0;
  transition: max-width 0.2s ease, opacity 0.15s ease, margin-left 0.2s ease;

  ${ListCardBtn}:hover:not(:disabled) & {
    max-width: 8rem;
    opacity: 1;
    margin-left: 0.3rem;
  }
`;

const SidebarRenameInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 0.15rem 0.4rem;
  border: 1px solid var(--color-accent);
  border-radius: 4px;
  font-size: 0.85rem;
  color: var(--color-text);
  background: var(--color-surface);
  &:focus { outline: none; }
`;

const NewListRow = styled.div`
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid var(--color-border-subtle);
`;

const NewListInput = styled.input`
  width: 100%;
  padding: 0.4rem 0.6rem;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 0.85rem;
  color: var(--color-text);
  background: var(--color-surface);
  box-sizing: border-box;

  &:focus { outline: none; border-color: var(--color-accent); }
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const EmptyState = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  box-shadow: var(--shadow-card);
  padding: 3rem;
  text-align: center;
  color: var(--color-text-secondary);
`;

const MovieCard = styled.div`
  background: var(--color-surface);
  border-radius: 10px;
  box-shadow: var(--shadow-card);
  padding: 1rem 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
`;

const MovieInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const MovieTitle = styled.a`
  font-weight: 600;
  color: var(--color-text);
  text-decoration: none;
  font-size: 1rem;
  display: block;
  margin-bottom: 0.25rem;

  &:hover { color: var(--color-accent); text-decoration: underline; }
`;

const MovieMeta = styled.div`
  color: var(--color-text-secondary);
  font-size: 0.83rem;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: var(--color-text-muted);
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0.2rem 0.4rem;
  margin-left: 0.75rem;
  border-radius: 4px;
  flex-shrink: 0;

  &:hover { color: #e53e3e; background: #fff5f5; }
`;

const ErrorMsg = styled.div`
  background: #fed7d7;
  color: #c53030;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

// --- Component ---

const WatchlistPage: React.FC = () => {
  const [watchlists, setWatchlists] = useState<WatchlistResponse[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [movies, setMovies] = useState<WatchlistMovie[]>([]);
  const [loadingMovies, setLoadingMovies] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [error, setError] = useState('');

  const fetchLists = useCallback(async () => {
    try {
      const lists = await apiService.getWatchlists();
      setWatchlists(lists);
      setSelectedId(prev => prev ?? (lists[0]?.id ?? null));
    } catch {
      setError('Failed to load watchlists');
    }
  }, []);

  useEffect(() => { fetchLists(); }, [fetchLists]);

  const fetchMoviesQuiet = useCallback(() => {
    if (!selectedId) return;
    apiService.getWatchlistMovies(selectedId).then(data => setMovies(data.movies)).catch(() => {});
  }, [selectedId]);

  useRealtimeRefetch(['watchlists', 'watchlist_movies'], () => {
    fetchLists();
    fetchMoviesQuiet();
  });

  useEffect(() => {
    if (!selectedId) { setMovies([]); return; }
    setLoadingMovies(true);
    apiService.getWatchlistMovies(selectedId)
      .then(data => setMovies(data.movies))
      .catch(() => setError('Failed to load movies'))
      .finally(() => setLoadingMovies(false));
  }, [selectedId]);

  const handleCreateList = async (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    const name = newListName.trim();
    if (!name) return;
    try {
      const created = await apiService.createWatchlist(name);
      setWatchlists(prev => [...prev, created]);
      setSelectedId(created.id);
      setNewListName('');
    } catch {
      setError('Failed to create watchlist');
    }
  };

  const handleDeleteList = async (id: string, name: string) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await apiService.deleteWatchlist(id);
      const remaining = watchlists.filter(w => w.id !== id);
      setWatchlists(remaining);
      if (selectedId === id) setSelectedId(remaining[0]?.id ?? null);
    } catch {
      setError('Failed to delete watchlist');
    }
  };

  const handleRename = async () => {
    if (!renamingId) return;
    const name = renameValue.trim();
    if (!name) return;
    try {
      await apiService.renameWatchlist(renamingId, name);
      setWatchlists(prev => prev.map(w => w.id === renamingId ? { ...w, name } : w));
      setRenamingId(null);
    } catch {
      setError('Failed to rename watchlist');
    }
  };

  const handleRemoveMovie = async (tmdbId: number) => {
    if (!selectedId) return;
    try {
      await apiService.removeMovieFromWatchlist(selectedId, tmdbId);
      setMovies(prev => prev.filter(m => m.tmdb_id !== tmdbId));
      setWatchlists(prev => prev.map(w =>
        w.id === selectedId ? { ...w, movie_count: w.movie_count - 1 } : w
      ));
    } catch {
      setError('Failed to remove movie');
    }
  };

  const selectedList = watchlists.find(w => w.id === selectedId);

  return (
    <Container>
      {error && <ErrorMsg onClick={() => setError('')}>{error} ✕</ErrorMsg>}

      <Layout>
        <Sidebar>
          <SidebarHeader>My Lists</SidebarHeader>
          <NewListRow>
            <NewListInput
              placeholder="New list… (press Enter)"
              value={newListName}
              onChange={e => setNewListName(e.target.value)}
              onKeyDown={handleCreateList}
            />
          </NewListRow>
          {watchlists.map(w => (
            <ListItem
              key={w.id}
              $active={w.id === selectedId}
              onClick={() => { if (renamingId !== w.id) { setSelectedId(w.id); setRenamingId(null); } }}
            >
              {renamingId === w.id ? (
                <>
                  <SidebarRenameInput
                    value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleRename();
                      if (e.key === 'Escape') setRenamingId(null);
                    }}
                    onClick={e => e.stopPropagation()}
                    autoFocus
                  />
                  <ListItemBtns style={{ opacity: 1 }}>
                    <ListCardBtn onClick={e => { e.stopPropagation(); handleRename(); }}>✓</ListCardBtn>
                    <ListCardBtn onClick={e => { e.stopPropagation(); setRenamingId(null); }}>✕</ListCardBtn>
                  </ListItemBtns>
                </>
              ) : (
                <>
                  <ListName $active={w.id === selectedId}>{w.name}</ListName>
                  <ListCount>{w.movie_count}</ListCount>
                  <ListItemBtns>
                    <ListCardBtn
                      onClick={e => {
                        e.stopPropagation();
                        setRenamingId(w.id);
                        setRenameValue(w.name);
                      }}
                    >
                      ✎<ListBtnLabel>Rename</ListBtnLabel>
                    </ListCardBtn>
                    <ListCardBtn
                      $variant="danger"
                      onClick={e => {
                        e.stopPropagation();
                        handleDeleteList(w.id, w.name);
                      }}
                    >
                      ✕<ListBtnLabel>Delete</ListBtnLabel>
                    </ListCardBtn>
                  </ListItemBtns>
                </>
              )}
            </ListItem>
          ))}
        </Sidebar>

        <MainContent>
          {!selectedList ? (
            <EmptyState>
              <h3>No watchlists yet</h3>
              <p>Create your first list using the input on the left.</p>
            </EmptyState>
          ) : loadingMovies ? (
            <EmptyState>Loading…</EmptyState>
          ) : movies.length === 0 ? (
            <EmptyState>
              <p>No movies yet. Add some from the <strong>Recommendations</strong> page.</p>
            </EmptyState>
          ) : (
            movies.map(m => (
              <MovieCard key={m.tmdb_id}>
                <MovieInfo>
                  <MovieTitle
                    href={`https://www.themoviedb.org/movie/${m.tmdb_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {m.title}
                  </MovieTitle>
                  <MovieMeta>
                    {m.release_date ? new Date(m.release_date).getFullYear() : ''}
                    {m.imdb_score ? ` · ⭐ ${m.imdb_score}` : ''}
                    {m.director_name ? ` · ${m.director_name}` : ''}
                    {m.mpaa_rating ? ` · ${m.mpaa_rating}` : ''}
                  </MovieMeta>
                </MovieInfo>
                <RemoveButton onClick={() => handleRemoveMovie(m.tmdb_id)} title="Remove from list">
                  ✕
                </RemoveButton>
              </MovieCard>
            ))
          )}
        </MainContent>
      </Layout>
    </Container>
  );
};

export default WatchlistPage;
